import { ErrorBoundary } from '@/components/Rollbar';
import {
  useDefaultPagination,
  useInvoicesTransaction,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoicesTransaction';
import { useTranslation } from '@/i18n';
import { OpenFeatureProvider } from '@moneyforward/user-flex-feature-hooks';

import { Wrapper } from '@/components/OpenFeature/Wrapper';
import { useFFContext } from '@/components/OpenFeature/hooks/useFFContext';
import { useInvoiceTransactionColumns } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoiceTransactionColumns';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { FC, Suspense, memo, useMemo } from 'react';
import { useColumns } from './Columns';
import { ErrorTransactionsDetailTable } from './ErrorTransactionsDetailTable';
import { LoadingTransactionsDetailTable } from './LoadingTransactionsDetailTable';
import {
  InternalTableProps,
  TransactionsDetailTable,
} from './TransactionsDetailTable';

type InnerListProps = {
  reportId: string;
};

const InnerList: FC<InnerListProps> = memo(({ reportId }) => {
  const { data } = useInvoicesTransaction(reportId);
  const columns = useInvoiceTransactionColumns(reportId);

  const { t } = useTranslation();
  const pagination = useDefaultPagination();
  const table: InternalTableProps = useMemo(() => {
    return {
      data: data ?? [],
      columns: columns ?? [],
      emptyText: <>{t('transactions_detail_empty_message')}</>,
    };
  }, [data, columns, t]);

  return <TransactionsDetailTable table={table} pagination={pagination} />;
});
InnerList.displayName = 'InnerList';

// Legacy component (use if FF is disabled)
// TODO: Delete legacy component when FF is removed
const InnerListLegacy: FC<InnerListProps> = memo(({ reportId }) => {
  const { data } = useInvoicesTransaction(reportId);
  const columns = useColumns();

  const { t } = useTranslation();
  const pagination = useDefaultPagination();
  const table: InternalTableProps = useMemo(() => {
    return {
      data: data ?? [],
      columns: columns ?? [],
      emptyText: <>{t('transactions_detail_empty_message')}</>,
    };
  }, [data, columns, t]);

  return <TransactionsDetailTable table={table} pagination={pagination} />;
});
InnerListLegacy.displayName = 'InnerListLegacy';

// Wrapper to switch between "old" and "new" components depending on feature flag.
// TODO: Delete wrappper when FF is removed. Use `InnerLIst` directly instead
const InnerListFFWrapper: FC<InnerListProps> = memo((props: InnerListProps) => {
  const isEnabledCustomProperties = useBooleanFlagValue(
    'custom-transaction-one',
    false
  );
  return isEnabledCustomProperties ? (
    <InnerList {...props} />
  ) : (
    <InnerListLegacy {...props} />
  );
});
InnerListFFWrapper.displayName = 'InnerListFFWrapper';

type TransactionsDetailProps = {
  reportId: string;
};

export const TransactionsDetail: FC<TransactionsDetailProps> = memo(
  ({ reportId }) => {
    const context = useFFContext();

    return (
      <Wrapper isProto={false}>
        <OpenFeatureProvider
          pollInterval={-1}
          context={context}
          domain='ap-frontend-custom-transaction-one'
        >
          <ErrorBoundary fallback={ErrorTransactionsDetailTable}>
            <Suspense fallback={<LoadingTransactionsDetailTable />}>
              <InnerListFFWrapper reportId={reportId} />
            </Suspense>
          </ErrorBoundary>
        </OpenFeatureProvider>
      </Wrapper>
    );
  }
);
TransactionsDetail.displayName = 'TransactionsDetail';
